import React, { useCallback, useEffect, useState } from 'react';
import { AppRemixIcon, AppNoInfo } from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import Scrollbars from 'react-custom-scrollbars';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import { axiosPath } from '../../../../../routing/helpers/router';
import colors from '../../../../../constants/colors';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../components/helpers/Translator';
import EvidenceEditForm from './EvidenceEditForm';
import minions from '../../../../../minions/minions';
import ObligationNextDateSettings from './ObligationNextDateSettings';
import PrevisionEditForm from './PrevisionEditForm';
import ResetAllPrevisions from './ResetAllPrevisions';
import ResetFuturePrevisions from './ResetFuturePrevisions';
import { getState, getNextDateHuman } from '../tools/resourceManagerTools';
import ToggleValidatePeriod from './ToggleValidatePeriod';
import DebugMultiPressAlert from '../../../../components/debug/DebugMultiPressAlert';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import { path } from '@/routing/helpers/router';

export default function ObligationCard({
  resourceObligationIndex,
  resourceObligation,
  elementID,
  view,
  obligationIDsInPlant,
  loadResourceObligationTab,
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const isAdmin = useSelector((state) => state?.auth?.user?.isAdmin);
  const resLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2?.[elementID],
  );
  const userBridge = useSelector((state) => state.module[moduleBridgeHex].userBridge);
  const [selectedPrevision, setSelectedPrevision] = useState();
  const [isEditEvidenceMode, setIsEditEvidenceMode] = useState(false);
  const [isEditPrevisionMode, setIsEditPrevisionMode] = useState(false);
  const [isPrevisionPanelOpened, setIsPrevisionPanelOpened] = useState(false);

  useEffect(() => {
    if (selectedPrevision) {
      setSelectedPrevision(
        resourceObligation?.previsions?.find((p) => p?.id === selectedPrevision?.id),
      );
    }
  }, [resourceObligation]);

  // > REDIRECT TO PLANT WITH OBLIGATION FILTER
  const createFilterAndRedirect = useCallback(
    async (obligationID) =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_generateFilterHexCodeWithObligationIDs',
          ),
          {
            moduleBridgeHex: resLevel2?.plant?.moduleBridgeHex,
            obligationID,
          },
        )
        .then(({ data }) => {
          if (data?.hexCode) {
            window.open(
              path({
                name: 'user_module_legalCompliance_evaluation',
                params: {
                  moduleBridgeHex: resLevel2?.plant?.moduleBridgeHex,
                  plantHex: resLevel2?.plant?.plantHex,
                  page: 1,
                  filtersHex: data?.hexCode,
                },
              }),
            );
            // window.open(
            //   `${global.urlData.rootProject}r/legal-compliance/${resLevel2?.plant?.moduleBridgeHex}/evaluation/${resLevel2?.plant?.plantHex}/1/${data?.hexCode}/`,
            //   "_blank"
            // );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [resLevel2, elementID],
  );
  // < REDIRECT TO PLANT WITH OBLIGATION FILTER

  const handleDownloadFile = useCallback(
    async (url) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_resourceObligation_prevision_evidence_file_download',
          ),
          {
            moduleBridgeHex,
            evidenceID: selectedPrevision?.evidence?.id,
            url,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(data, '_blank');
          }
        })
        .catch((e) => errorHandler(e));
    },
    [moduleBridgeHex, selectedPrevision],
  );

  return (
    <div key={resourceObligation?.obligation?.id}>
      <Card
        className="list-group-item p-0"
        $state={getState(resourceObligation?.state)?.bgColor}
      >
        <div
          className="rs-status pointer"
          onClick={() => setIsPrevisionPanelOpened(!isPrevisionPanelOpened)}
        >
          <AppRemixIcon
            icon={getState(resourceObligation?.state)?.icon}
            color={getState(resourceObligation?.state)?.fgColor}
            size={20}
          />
        </div>
        {view === 'resource' ? (
          resourceObligation?.obligation?.hasFormat ? (
            <div
              className="rs-obligationText"
              dangerouslySetInnerHTML={{
                __html: resourceObligation?.obligation?.obligation,
              }}
            />
          ) : (
            <div className="rs-obligationText" style={{ whiteSpace: 'pre-line' }}>
              {resourceObligation?.obligation?.obligation}
            </div>
          )
        ) : (
          <div
            className="rs-obligationText"
            dangerouslySetInnerHTML={{
              __html: resourceObligation?.resource?.name,
            }}
          />
        )}
        <div className="rs-nextDate">
          <AppRemixIcon
            icon="spam"
            size={12}
            className="discordance"
            color={
              resourceObligation?.obligation?.nextDateDefaultSettings?.periodType !==
                null &&
              (resourceObligation?.obligation?.nextDateDefaultSettings?.periodType !==
                resourceObligation?.nextDateSettings?.periodType ||
                resourceObligation?.obligation?.nextDateDefaultSettings?.period !==
                  resourceObligation?.nextDateSettings?.period)
                ? colors.danger
                : colors.grey400
            }
            title={
              resourceObligation?.obligation?.nextDateDefaultSettings?.periodType !==
              null
                ? getNextDateHuman(
                    resourceObligation?.obligation?.nextDateDefaultSettings,
                  )
                : ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.no.disagreement.legal.periodicity"
                    />,
                  )
            }
          />

          {resourceObligation?.nextDateSettings ? (
            <>
              <ToggleValidatePeriod resourceObligation={resourceObligation} />
              {getNextDateHuman(resourceObligation?.nextDateSettings)}
            </>
          ) : (
            (![2, 3].includes(userBridge?.permissions?.level) ||
              (userBridge?.permissions?.level === 3 &&
                userBridge?.permissions?.recalculateFuturePeriods)) && (
              <ObligationNextDateSettings
                resourceObligation={resourceObligation}
                loadObligationTab={loadResourceObligationTab}
                elementID={elementID}
                size="md"
              />
            )
          )}
        </div>
        <div className="rs-calendar">
          {resourceObligation?.previsions?.[0] &&
            !resourceObligation?.previsions?.[0]?.currentPeriod && (
              <div
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-resourceManager"
                    name="resource.future.period"
                  />,
                )}
              >
                <AppRemixIcon
                  icon="calendar-schedule"
                  color={
                    resourceObligation?.previsions?.[0]?.state !== 0
                      ? getState(resourceObligation?.previsions?.[0]?.state)?.bgColor
                      : '#000'
                  }
                  className="mr-2"
                />
                <span
                  key={resourceObligation?.previsions?.[0]?.id}
                  style={{
                    color:
                      resourceObligation?.previsions?.[0]?.state !== 0
                        ? getState(resourceObligation?.previsions?.[0]?.state)
                            ?.bgColor
                        : '#000',
                  }}
                  className="mr-2"
                >
                  {resourceObligation?.previsions?.[0]?.startDate} -{' '}
                  {resourceObligation?.previsions?.[0]?.endDate}
                </span>
                <AppRemixIcon
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name={
                        resourceObligation?.previsions?.[0]?.evidence?.urls?.length >
                        0
                          ? 'associated.files'
                          : 'no.associated.files'
                      }
                    />,
                  )}
                  icon="attachment-2"
                  noVariants
                  color={
                    resourceObligation?.previsions?.[0]?.evidence?.urls?.length > 0
                      ? colors.primary
                      : colors.grey400
                  }
                />
              </div>
            )}
          {resourceObligation?.previsions?.[1] &&
            resourceObligation?.previsions?.[1]?.currentPeriod && (
              <div
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-resourceManager"
                    name="resource.current.period"
                  />,
                )}
              >
                <AppRemixIcon
                  icon="calendar"
                  color={
                    getState(resourceObligation?.previsions?.[1]?.state)?.bgColor
                  }
                  className="mr-2"
                />
                <span
                  key={resourceObligation?.previsions?.[1]?.id}
                  style={{
                    color: getState(resourceObligation?.previsions?.[1]?.state)
                      ?.bgColor,
                  }}
                  className="mr-2"
                >
                  {resourceObligation?.previsions?.[1]?.startDate} -{' '}
                  {resourceObligation?.previsions?.[1]?.endDate}
                </span>
                <AppRemixIcon
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name={
                        resourceObligation?.previsions?.[1]?.evidence?.urls?.length >
                        0
                          ? 'associated.files'
                          : 'no.associated.files'
                      }
                    />,
                  )}
                  icon="attachment-2"
                  noVariants
                  color={
                    resourceObligation?.previsions?.[1]?.evidence?.urls?.length > 0
                      ? colors.primary
                      : colors.grey400
                  }
                />
              </div>
            )}
        </div>
        <div className="rs-iconBlock">
          <div className="rs-iconGrid">
            <AppRemixIcon
              icon="eye"
              color={
                resLevel2?.plant?.moduleBridgeHex
                  ? isAdmin
                    ? obligationIDsInPlant?.includes(
                        resourceObligation?.obligation?.id,
                      )
                      ? colors.primary
                      : colors.danger
                    : colors.primary
                  : colors.grey400
              }
              className={resLevel2?.plant?.moduleBridgeHex ? 'pointer' : ''}
              onClick={() =>
                resLevel2?.plant?.moduleBridgeHex &&
                createFilterAndRedirect(resourceObligation?.obligation?.id)
              }
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-resourceManager"
                  name="resource.go.obligation.installation"
                />,
              )}
            />
            <AppRemixIcon
              icon="calendar-close"
              color={
                resourceObligation?.nextDateSettings?.noNextDate
                  ? colors.primary
                  : colors.grey400
              }
              filled={resourceObligation?.nextDateSettings?.noNextDate}
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-resourceManager"
                  name={
                    resourceObligation?.nextDateSettings?.noNextDate
                      ? 'resource.no.regularity'
                      : 'resource.periodical'
                  }
                />,
              )}
            />
            {view === 'resource' && (
              <AppRemixIcon
                icon="notification-2"
                color={
                  resourceObligation?.lastThreeMonthsModifiedObligations > 0
                    ? colors.modifiedObligations
                    : colors.grey400
                }
                filled={resourceObligation?.lastThreeMonthsModifiedObligations > 0}
                title={
                  resourceObligation?.lastThreeMonthsModifiedObligations > 0
                    ? ReactDOMServer.renderToString(
                        <Translator
                          file="module-resourceManager"
                          name="resource.new.modified.obligations.last.months"
                        />,
                      )
                    : ReactDOMServer.renderToString(
                        <Translator
                          file="module-resourceManager"
                          name="resource.obligation.not.new.modified.last.months"
                        />,
                      )
                }
              />
            )}

            {(![2, 3].includes(userBridge?.permissions?.level) ||
              (userBridge?.permissions?.level === 3 &&
                userBridge?.permissions?.recalculateFuturePeriods)) && (
              <>
                <ResetFuturePrevisions
                  resourceObligationID={resourceObligation?.id}
                  elementID={elementID}
                />

                <ResetAllPrevisions
                  resourceObligation={resourceObligation}
                  elementID={elementID}
                />

                <ObligationNextDateSettings
                  resourceObligation={resourceObligation}
                  loadObligationTab={loadResourceObligationTab}
                  elementID={elementID}
                  size="sm"
                />
              </>
            )}
          </div>
          <div className="rs-code">
            <DebugMultiPressAlert
              info={`ResourceObligationID: ${resourceObligation.id}`}
            >
              {view === 'resource'
                ? resourceObligation?.obligation?.hex
                : resourceObligation?.resource?.hex}
            </DebugMultiPressAlert>
          </div>
        </div>
      </Card>
      <UnmountClosed isOpened={isPrevisionPanelOpened}>
        <ExpendedPanel>
          {resourceObligation?.nextDateSettings ? (
            <PrevisionContainer>
              <SidebarMenu>
                {resourceObligation?.previsions?.length > 0 ? (
                  <Scrollbars style={{ height: '340px' }}>
                    <ul>
                      {resourceObligation?.previsions?.map((p) => (
                        <li
                          key={p.id}
                          className={
                            selectedPrevision && selectedPrevision?.id === p?.id
                              ? 'ev-selected'
                              : p?.state === 1
                                ? 'text-success'
                                : p?.state === 2
                                  ? 'text-warning'
                                  : p?.state === 3
                                    ? 'text-danger'
                                    : p?.state === 0
                                      ? ''
                                      : 'text-warning'
                          }
                          onClick={() => {
                            setSelectedPrevision(p);
                            setIsEditEvidenceMode(false);
                            setIsEditPrevisionMode(false);
                          }}
                        >
                          <div>
                            <AppRemixIcon
                              className="mr-2"
                              icon={
                                p?.state === 1
                                  ? 'check'
                                  : [3, 2].includes(p?.state)
                                    ? 'alert'
                                    : 'calendar-schedule'
                              }
                              color={
                                p?.state === 0
                                  ? '#000'
                                  : p?.state === 1
                                    ? colors.success
                                    : p?.state === 2
                                      ? colors.state2
                                      : colors.danger
                              }
                            />
                            <span className="cd-date">
                              {p.startDate} - {p.endDate}
                            </span>
                          </div>
                          <div>
                            {p?.currentPeriod && (
                              <AppRemixIcon
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.current.period"
                                  />,
                                )}
                                icon="map-pin"
                                className="mr-2"
                                color={colors.primary}
                              />
                            )}
                            <AppRemixIcon
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name={
                                    p?.evidence?.urls?.length > 0
                                      ? 'associated.files'
                                      : 'no.associated.files'
                                  }
                                />,
                              )}
                              icon="attachment-2"
                              noVariants
                              color={
                                p?.evidence?.urls?.length > 0
                                  ? colors.primary
                                  : colors.grey400
                              }
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Scrollbars>
                ) : (
                  <AppNoInfo
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="without.events"
                      />,
                    )}
                    color={colors.primaryLight}
                    icon={
                      <AppRemixIcon
                        size={24}
                        icon="file-search"
                        color={colors.primaryLight}
                      />
                    }
                  />
                )}
              </SidebarMenu>
              <Content>
                {selectedPrevision ? (
                  <>
                    {isEditEvidenceMode ? (
                      <EvidenceEditForm
                        resourceObligationIndex={resourceObligationIndex}
                        resourceObligationID={elementID}
                        selectedPrevision={selectedPrevision}
                        onCancel={() => setIsEditEvidenceMode(false)}
                        onRemove={() => {
                          setSelectedPrevision({
                            ...selectedPrevision,
                            evidence: null,
                          });
                          setIsEditEvidenceMode(false);
                        }}
                        onSave={() => {
                          setIsEditEvidenceMode(false);
                        }}
                        elementID={elementID}
                      />
                    ) : isEditPrevisionMode ? (
                      <PrevisionEditForm
                        resourceObligationIndex={resourceObligationIndex}
                        resourceObligationID={elementID}
                        selectedPrevision={selectedPrevision}
                        onCancel={() => setIsEditPrevisionMode(false)}
                        onSave={() => {
                          setIsEditPrevisionMode(false);
                        }}
                        elementID={elementID}
                      />
                    ) : (
                      <div className="container-fluid">
                        <div className="row">
                          <div className="col-12">
                            <h3>
                              {selectedPrevision?.evidence && (
                                <>
                                  {' '}
                                  <Translator
                                    file="module-legalCompliance"
                                    name="attached.evidence"
                                  />{' '}
                                  ({selectedPrevision.startDate})
                                </>
                              )}
                              <span
                                className="float-right pointer mr-2"
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-legalCompliance"
                                    name="close"
                                  />,
                                )}
                              >
                                <AppRemixIcon
                                  icon="close"
                                  size={16}
                                  onClick={() => {
                                    setSelectedPrevision(undefined);
                                    setIsEditEvidenceMode(false);
                                  }}
                                />
                              </span>
                              {userBridge?.permissions?.level !== 2 && (
                                <span
                                  className="float-right pointer mr-2"
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-resourceManager"
                                      name="resource.edit.period"
                                    />,
                                  )}
                                >
                                  <AppRemixIcon
                                    icon="calendar-schedule"
                                    size={16}
                                    onClick={() => setIsEditPrevisionMode(true)}
                                  />
                                </span>
                              )}
                              {selectedPrevision?.evidence &&
                                userBridge?.permissions?.level !== 2 && (
                                  <span
                                    className="float-right pointer mr-2"
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-resourceManager"
                                        name="resource.edit.evidence"
                                      />,
                                    )}
                                  >
                                    <AppRemixIcon
                                      icon="edit"
                                      size={16}
                                      onClick={() => setIsEditEvidenceMode(true)}
                                    />
                                  </span>
                                )}
                            </h3>
                          </div>
                        </div>
                        {selectedPrevision?.evidence ? (
                          <div className="row">
                            <div
                              className={`col-lg-${
                                selectedPrevision?.evidence?.advancedChecklist
                                  ?.length > 0
                                  ? '6'
                                  : '12'
                              }`}
                            >
                              <EvidenceInfo className="mt-4">
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="name"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedPrevision?.evidence?.name}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="associated.files"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedPrevision?.evidence?.urls?.length > 0 ? (
                                      selectedPrevision?.evidence?.urls.map((url) => (
                                        <div
                                          key={url}
                                          onClick={() => handleDownloadFile(url)}
                                          className="pointer"
                                          title={ReactDOMServer.renderToString(
                                            <Translator
                                              file="module-legalCompliance"
                                              name="download.file"
                                            />,
                                          )}
                                        >
                                          {minions.files.fileIcon(
                                            url,
                                            true,
                                            null,
                                            'mr-2',
                                          )}
                                          {url}
                                        </div>
                                      ))
                                    ) : (
                                      <span>
                                        <Translator
                                          file="module-legalCompliance"
                                          name="no.associated.files"
                                        />
                                      </span>
                                    )}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="user"
                                      name="user.obligation.evidence.date"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedPrevision?.evidence?.date ? (
                                      selectedPrevision?.evidence?.date
                                    ) : (
                                      <span className="text-muted">
                                        <Translator file="user" name="user.no-date" />
                                      </span>
                                    )}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="module.lc.evidence.description"
                                    />
                                  </span>
                                  <span
                                    className="eventinfo-content"
                                    style={{
                                      maxWidth: 'calc(100vw - 900px)',
                                    }}
                                  >
                                    {selectedPrevision?.evidence?.description}
                                  </span>
                                </li>
                              </EvidenceInfo>
                            </div>
                            {selectedPrevision?.evidence?.advancedChecklist?.length >
                              0 && (
                              <div className="col-6">
                                <h6>Lista de chequeo</h6>
                                <AdvancedEvidenceList>
                                  {selectedPrevision?.evidence?.advancedChecklist
                                    ?.filter((ac) => ac.checked !== undefined)
                                    ?.map((ac) => (
                                      <li
                                        key={ac?.id}
                                        className={`text-${
                                          ac?.checked ? 'success' : 'danger'
                                        }`}
                                      >
                                        <AppRemixIcon
                                          icon={ac?.checked ? 'check' : 'close'}
                                        />
                                        <span>{ac?.name}</span>
                                      </li>
                                    ))}
                                </AdvancedEvidenceList>
                              </div>
                            )}
                          </div>
                        ) : (
                          <AppNoInfo
                            color={colors.primaryLight}
                            icon={
                              <AppRemixIcon
                                size={40}
                                icon="attachment-2"
                                color={colors.primaryLight}
                                noVariants
                              />
                            }
                            text={
                              <p>
                                <div className="mb-4">
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.no.associated.evidence"
                                  />
                                </div>
                                {userBridge?.permissions?.level !== 2 && (
                                  <div
                                    className="btn btn-xs btn-primary"
                                    onClick={() => setIsEditEvidenceMode(true)}
                                  >
                                    <Translator
                                      file="module-resourceManager"
                                      name="resource.associate"
                                    />
                                  </div>
                                )}
                              </p>
                            }
                          />
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <AppNoInfo
                    color={colors.primaryLight}
                    icon={
                      <AppRemixIcon
                        size={40}
                        icon="file-search"
                        color={colors.primaryLight}
                      />
                    }
                    text={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.select.date"
                      />,
                    )}
                  />
                )}
              </Content>
            </PrevisionContainer>
          ) : (
            <>
              {userBridge?.permissions?.level !== 2 && (
                <ObligationNextDateSettings
                  resourceObligation={resourceObligation}
                  showAsIcon={false}
                  loadObligationTab={loadResourceObligationTab}
                  elementID={elementID}
                  size="lg"
                />
              )}
            </>
          )}
        </ExpendedPanel>
      </UnmountClosed>
    </div>
  );
}

const Card = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 190px 210px 90px;
  gap: 10px;
  height: 60px;
  overflow-y: scroll;
  .rs-status {
    display: flex;
    max-height: 60px;
    background-color: ${(props) => props.$state};
    align-items: center;
    justify-content: center;
  }
  .rs-obligationText {
    border-right: 1px solid #ccc;
    overflow-y: scroll;
  }
  .rs-nextDate {
    padding: 10px;
    padding-left: 0;
    border-right: 1px solid #ccc;
    font-size: 11px;
    position: relative;
    h3 {
      font-size: 13px;
      ${colors.primaryLight}
    }
    i.discordance {
      position: absolute;
      right: 4px;
      top: 0;
    }
  }
  .rs-iconBlock {
    display: flex;
    flex-direction: column;
    padding: 2px;
    .rs-iconGrid {
      display: grid;
      grid-template-columns: 20px 20px 20px 20px;
      grid-template-rows: 20px 20px 20px 20px 20px 20px;
      height: 39px;
    }
    .rs-code {
      text-transform: uppercase;
      text-align: center;
      font-weight: bold;
      color: ${colors.primaryLight};
      font-size: 10px;
    }
  }
`;

const PrevisionContainer = styled.div`
  height: 340px;
  display: grid;
  grid-template-columns: 300px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .event-type {
    margin: 4px 0 2px 6px;
    font-size: 13px;
    text-transform: uppercase;
  }
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;

      &.ev-selected {
        background: ${colors.grey300};
      }
      .cd-date {
        font-size: 12px;
      }
    }
    li:last-child {
      border-bottom: 0;
    }
  }
`;

const Content = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 340px;
  .row-no-margin > .row {
    margin: 0;
  }
  h3 {
    font-size: 13px;
    margin: 0px 0px 6px;
    font-weight: bold;
  }
`;

const EvidenceInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;
const ExpendedPanel = styled.div`
  border-style: solid;
  border-color: ${colors.grey300};
  border-width: 0 1px 1px 1px;
  border-radius: 0 0 4px 4px;
  margin-bottom: 10px;
`;

const AdvancedEvidenceList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    gap: 5px;
    & > div {
      flex-grow: 1;
    }
  }
`;
